import {
  DimensionIconCategory,
  SHOW_DIMENSION_ICON,
  SHOW_DIMENSION_ICON_CATEGORY_KEYS,
  SHOW_DIMENSION_ICON_MAP,
  normalizeDimensionIconKey,
  type TDimensionIconCategory,
} from '@dbl/core/chartEditor';
import sortableDragZh from '@/pages/datalab/_locales/zh-CN/sortableDragComponent';
import sortableDragEn from '@/pages/datalab/_locales/en-US/sortableDragComponent';
import { formatCountryDimensionIconName } from '@/pages/datalab/utils/dimensionCountryIconLabel';

const I18N_FULL_PREFIX = 'dbl_sortable_drag_dimension_icon_name_';

function buildKeyToCategoryMap(): Map<string, TDimensionIconCategory> {
  const map = new Map<string, TDimensionIconCategory>();
  for (const cat of SHOW_DIMENSION_ICON_CATEGORY_KEYS) {
    for (const raw of SHOW_DIMENSION_ICON[cat]) {
      const key = normalizeDimensionIconKey(raw);
      map.set(key, cat);
    }
  }
  return map;
}

export const DIMENSION_ICON_KEY_TO_CATEGORY =
  buildKeyToCategoryMap();

export const DIMENSION_ICON_ALL_PICKABLE_KEYS: string[] = Array.from(
  new Set(
    Array.from(DIMENSION_ICON_KEY_TO_CATEGORY.keys()).filter(
      (k) => SHOW_DIMENSION_ICON_MAP[k],
    ),
  ),
);

const haystackCache = new Map<string, string>();

function readSortableDragLabel(key: string, lang: 'zh' | 'en'): string {
  const id = `${I18N_FULL_PREFIX}${key}`;
  const src = lang === 'zh' ? sortableDragZh : sortableDragEn;
  const v = (src as Record<string, string | undefined>)[id];
  return v || '';
}

export function buildDimensionIconSearchHaystack(
  key: string,
  category: TDimensionIconCategory,
): string {
  const parts: string[] = [
    key,
    key.replace(/-/g, ''),
    key.replace(/_/g, ''),
  ];
  if (category === DimensionIconCategory.Country) {
    parts.push(
      formatCountryDimensionIconName(key, 'zh-CN'),
      formatCountryDimensionIconName(key, 'en-US'),
    );
  } else {
    parts.push(readSortableDragLabel(key, 'zh'), readSortableDragLabel(key, 'en'));
  }
  return parts.filter(Boolean).join(' ').toLowerCase();
}

export function getCachedDimensionIconSearchHaystack(key: string): string {
  if (!haystackCache.has(key)) {
    const cat = DIMENSION_ICON_KEY_TO_CATEGORY.get(key);
    const meta = SHOW_DIMENSION_ICON_MAP[key];
    if (!cat || !meta) {
      haystackCache.set(key, '');
      return '';
    }
    haystackCache.set(key, buildDimensionIconSearchHaystack(key, cat));
  }
  return haystackCache.get(key)!;
}

export function filterPickableDimensionIconsByQuery(query: string): string[] {
  const q = query.trim().toLowerCase();
  if (!q) {
    return [];
  }
  const out: string[] = [];
  for (const k of DIMENSION_ICON_ALL_PICKABLE_KEYS) {
    if (getCachedDimensionIconSearchHaystack(k).includes(q)) {
      out.push(k);
    }
  }
  return out;
}

export function sortDimensionIconKeysForDisplay(
  keys: string[],
  locale: string,
  labelForKey: (key: string) => string,
): string[] {
  return [...keys].sort((a, b) =>
    labelForKey(a).localeCompare(labelForKey(b), locale, {
      sensitivity: 'base',
    }),
  );
}
